import { useQuery, useQueryClient } from "@tanstack/react-query";
import { ChevronDown, ChevronUp, Download, Eye, Pencil, Plus, Search, SlidersHorizontal, Trash2 } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { api } from "../api";
import { styleWorkbook } from "../excel";
import { Button, ConfirmDialog, EmptyState, FormField, Modal, NumericInput, Pagination, Panel, SearchBox } from "../components/ui";
import { formatNumber } from "../numbers";
import { PageHeader } from "../components/shell";
import { CatalogSelect, useCatalogs } from "./boms";
import { CalculationDetailModal, createVoucherWorksheet, voucherSheetName } from "./calculation-vouchers";
import { formatDate } from "./catalogs";
import type { BomListItem, CalculationVoucher, PageMeta } from "../types";

type PlanLine = { productId: string; customerId: string; contractNo: string; bomVersionId: string; productionQuantity: string };
type CalculationResponse = { id: string; code: string };
type VoucherList = { items: CalculationVoucher[]; meta: PageMeta };
type BomListResponse = { items: BomListItem[]; meta: PageMeta };
type VoucherModalState = { id: string; mode: "view" | "edit" } | null;

const blankLine = (): PlanLine => ({ productId: "", customerId: "", contractNo: "", bomVersionId: "", productionQuantity: "" });

export function CalculationsPage() {
  const catalogs = useCatalogs(["product", "customer"]);
  const client = useQueryClient();
  const [search, setSearch] = useState("");
  const [contractNo, setContractNo] = useState("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [productId, setProductId] = useState("");
  const [customerId, setCustomerId] = useState("");
  const [official, setOfficial] = useState(false);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [page, setPage] = useState(1);
  const [formOpen, setFormOpen] = useState(false);
  const [selected, setSelected] = useState<VoucherModalState>(null);
  const [selectedVouchers, setSelectedVouchers] = useState<Record<string, CalculationVoucher>>({});
  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState("");
  const selectAllRef = useRef<HTMLInputElement>(null);
  const vouchers = useQuery({
    queryKey: ["calculation-vouchers", page, search, contractNo, from, to, productId, customerId, official],
    queryFn: () => api<VoucherList>(`/calculations?page=${page}&pageSize=25&q=${encodeURIComponent(search)}&contractNo=${encodeURIComponent(contractNo)}${from ? `&from=${from}` : ""}${to ? `&to=${to}` : ""}${productId ? `&productId=${productId}` : ""}${customerId ? `&customerId=${customerId}` : ""}${official ? "&official=true" : ""}`),
  });

  useEffect(() => setPage(1), [search, contractNo, from, to, productId, customerId, official]);

  const extraFilterCount = [contractNo, from, to, productId, customerId, official].filter(Boolean).length;
  function clearFilters() {
    setSearch("");
    setContractNo("");
    setFrom("");
    setTo("");
    setProductId("");
    setCustomerId("");
    setOfficial(false);
  }

  const visibleVouchers = vouchers.data?.items ?? [];
  const selectedCount = Object.keys(selectedVouchers).length;
  const selectedVisibleCount = visibleVouchers.filter((voucher) => Boolean(selectedVouchers[voucher.id])).length;
  const allVisibleSelected = visibleVouchers.length > 0 && visibleVouchers.every((voucher) => Boolean(selectedVouchers[voucher.id]));

  useEffect(() => {
    if (selectAllRef.current) selectAllRef.current.indeterminate = selectedVisibleCount > 0 && !allVisibleSelected;
  }, [allVisibleSelected, selectedVisibleCount]);

  function toggleVoucher(voucher: CalculationVoucher) {
    setSelectedVouchers((current) => {
      const next = { ...current };
      if (next[voucher.id]) delete next[voucher.id];
      else next[voucher.id] = voucher;
      return next;
    });
  }

  function selectAllVisible() {
    setSelectedVouchers((current) => ({
      ...current,
      ...Object.fromEntries(visibleVouchers.map((voucher) => [voucher.id, voucher])),
    }));
  }

  function toggleAllVisible() {
    if (allVisibleSelected) {
      setSelectedVouchers((current) => {
        const next = { ...current };
        visibleVouchers.forEach((voucher) => delete next[voucher.id]);
        return next;
      });
      return;
    }
    selectAllVisible();
  }

  async function exportSelectedVouchers() {
    const selectedRows = Object.values(selectedVouchers);
    if (!selectedRows.length) return;
    setExporting(true);
    setExportError("");
    try {
      const XLSX = await import("xlsx-js-style");
      const workbook = XLSX.utils.book_new();
      const details = [];
      for (let index = 0; index < selectedRows.length; index += 5) {
        details.push(...await Promise.all(selectedRows.slice(index, index + 5).map((voucher) => api<CalculationVoucher>(`/calculations/${voucher.id}`))));
      }
      const usedSheetNames = new Set<string>();
      details.forEach((voucher) => {
        XLSX.utils.book_append_sheet(workbook, createVoucherWorksheet(XLSX, voucher), voucherSheetName(voucher, usedSheetNames));
      });
      styleWorkbook(XLSX, workbook, "Phiếu tính đã chọn");
      XLSX.writeFile(workbook, `phieu-tinh-da-chon-${new Date().toISOString().slice(0, 10)}.xlsx`);
    } catch (cause) {
      setExportError(cause instanceof Error ? cause.message : "Không thể xuất tệp Excel. Vui lòng thử lại.");
    } finally {
      setExporting(false);
    }
  }

  return <div className="page voucher-list-page">
    <PageHeader title="Danh sách phiếu tính" actions={<Button onClick={() => setFormOpen(true)}><Plus size={17} /> Tính mới</Button>} />
    <Panel className="voucher-search-panel">
      <div className="voucher-search-toolbar">
        <SearchBox value={search} onChange={setSearch} placeholder="Tìm theo mã phiếu, hợp đồng hoặc khách hàng" />
        <Button type="button" variant="outline" onClick={() => { setPage(1); void vouchers.refetch(); }}><Search size={16} /> Tìm phiếu</Button>
        <Button type="button" variant="outline" className={filtersOpen ? "filter-toggle selected" : "filter-toggle"} onClick={() => setFiltersOpen((value) => !value)}><SlidersHorizontal size={16} /> Lọc thêm{extraFilterCount ? ` (${extraFilterCount})` : ""}{filtersOpen ? <ChevronUp size={15} /> : <ChevronDown size={15} />}</Button>
        {extraFilterCount > 0 && <button type="button" className="clear-filters" onClick={clearFilters}>Xóa điều kiện lọc</button>}
      </div>
      {filtersOpen && <div className="voucher-filter-grid voucher-filter-grid-compact">
        <FormField label="Số hợp đồng"><input value={contractNo} onChange={(event) => setContractNo(event.target.value)} placeholder="Nhập chính xác hoặc một phần số hợp đồng" /></FormField>
        <CatalogSelect label="Khách hàng" value={customerId} onChange={setCustomerId} items={catalogs?.customer ?? []} />
        <CatalogSelect label="Sản phẩm" value={productId} onChange={setProductId} items={catalogs?.product ?? []} />
        <FormField label="Từ ngày"><input type="date" value={from} onChange={(event) => setFrom(event.target.value)} /></FormField>
        <FormField label="Đến ngày"><input type="date" value={to} onChange={(event) => setTo(event.target.value)} /></FormField>
        <label className="check-field voucher-filter-check"><input type="checkbox" checked={official} onChange={(event) => setOfficial(event.target.checked)} /> Chỉ xem phiếu dùng BOM đang áp dụng</label>
      </div>}
    </Panel>
    <Panel title="Phiếu tính" actions={<div className="voucher-selection-actions">
      <span>{selectedCount ? `Đã chọn ${formatNumber(selectedCount)} phiếu` : "Chưa chọn phiếu"}</span>
      <Button type="button" variant="outline" onClick={selectAllVisible} disabled={!visibleVouchers.length || allVisibleSelected}>Chọn tất cả</Button>
      <Button type="button" variant="outline" onClick={() => setSelectedVouchers({})} disabled={!selectedCount}>Bỏ chọn tất cả</Button>
      <Button type="button" variant="outline" onClick={() => void exportSelectedVouchers()} loading={exporting} disabled={!selectedCount}><Download size={16} /> Xuất Excel</Button>
    </div>}>
      {vouchers.isLoading ? <div className="loading-space">Đang tải phiếu tính…</div> : !vouchers.data?.items.length ? <EmptyState title="Chưa có phiếu tính phù hợp" detail="Bạn có thể điều chỉnh điều kiện tìm kiếm hoặc lập phiếu tính mới." action={<Button onClick={() => setFormOpen(true)}><Plus size={16} /> Tính mới</Button>} /> : <>
        <div className="table-scroll"><table className="voucher-history-table"><thead><tr><th className="row-select-cell"><input ref={selectAllRef} type="checkbox" aria-label="Chọn hoặc bỏ chọn tất cả phiếu đang hiển thị" checked={allVisibleSelected} onChange={toggleAllVisible} /></th><th>Mã phiếu</th><th>Thời gian lập</th><th>Số hợp đồng</th><th>Khách hàng</th><th>Sản phẩm</th><th>Lượng sản xuất</th><th>Cần mua / cấp</th><th>Trạng thái</th><th>Thao tác</th></tr></thead><tbody>
          {vouchers.data.items.map((voucher) => <tr key={voucher.id}>
            <td className="row-select-cell"><input type="checkbox" aria-label={`Chọn phiếu ${voucher.code}`} checked={Boolean(selectedVouchers[voucher.id])} onChange={() => toggleVoucher(voucher)} /></td><td className="code-cell">{voucher.code}</td><td>{formatDate(voucher.createdAt)}</td><td>{voucher.contractNo || "—"}</td><td>{voucher.customer?.name ?? "—"}</td><td>{voucher.product?.name ?? "—"}</td><td className="num-cell">{formatNumber(voucher.productionQuantity)}</td><td className="num-cell strong">{formatNumber(voucher.totalToSupply)}</td><td><span className={voucher.confirmedAt ? "status active" : "status draft"}>{voucher.confirmedAt ? "Đã chốt" : "Đang lập"}</span></td>
            <td className="row-actions"><button type="button" title="Xem chi tiết phiếu" aria-label={`Xem ${voucher.code}`} onClick={() => setSelected({ id: voucher.id, mode: "view" })}><Eye size={17} /></button>{!voucher.confirmedAt && <><button type="button" title="Điều chỉnh phiếu" aria-label={`Điều chỉnh ${voucher.code}`} onClick={() => setSelected({ id: voucher.id, mode: "edit" })}><Pencil size={17} /></button><DeleteVoucherAction voucher={voucher} onDeleted={() => { setSelectedVouchers((current) => { const next = { ...current }; delete next[voucher.id]; return next; }); setSelected(null); void client.invalidateQueries({ queryKey: ["calculation-vouchers"] }); }} /></>}</td>
          </tr>)}
        </tbody></table></div>
        <Pagination meta={vouchers.data.meta} onPage={setPage} />
      </>}
      {exportError && <p className="form-error">{exportError}</p>}
    </Panel>
    <CalculationFormModal open={formOpen} onClose={() => setFormOpen(false)} onCreated={(id) => { setFormOpen(false); setSelected({ id, mode: "edit" }); void client.invalidateQueries({ queryKey: ["calculation-vouchers"] }); }} />
    <CalculationDetailModal calculationId={selected?.id ?? null} open={Boolean(selected)} mode={selected?.mode} onClose={() => setSelected(null)} onUpdated={() => { void client.invalidateQueries({ queryKey: ["calculation-vouchers"] }); }} />
  </div>;
}

function DeleteVoucherAction({ voucher, onDeleted }: { voucher: CalculationVoucher; onDeleted(): void }) {
  const client = useQueryClient();
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  async function remove() {
    setBusy(true);
    setError("");
    try {
      await api(`/calculations/${voucher.id}`, { method: "DELETE" });
      await client.invalidateQueries({ queryKey: ["calculation-vouchers"] });
      setOpen(false);
      onDeleted();
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "Không thể xóa phiếu tính.");
    } finally {
      setBusy(false);
    }
  }
  return <><button type="button" title="Xóa phiếu tính" aria-label={`Xóa ${voucher.code}`} disabled={busy} onClick={() => { setError(""); setOpen(true); }}><Trash2 size={17} /></button><ConfirmDialog open={open} title={error ? "Không thể xóa phiếu" : "Xóa phiếu tính"} message={error || `Bạn có chắc muốn xóa phiếu ${voucher.code}? Phiếu nháp sẽ bị xóa vĩnh viễn.`} confirmLabel="Xóa phiếu" pending={busy} onClose={() => setOpen(false)} onConfirm={() => void remove()} /></>;
}

function CalculationFormModal({ open, onClose, onCreated }: { open: boolean; onClose(): void; onCreated(id: string): void }) {
  const catalogs = useCatalogs(["product", "customer", "warehouse"]);
  const client = useQueryClient();
  const [title, setTitle] = useState("");
  const [official, setOfficial] = useState(false);
  const [snapshotAt, setSnapshotAt] = useState("");
  const [warehouseIds, setWarehouseIds] = useState<string[]>([]);
  const [lines, setLines] = useState<PlanLine[]>([blankLine()]);
  const [running, setRunning] = useState(false);
  const [error, setError] = useState("");
  const boms = useQuery({ queryKey: ["calculation-boms", official], queryFn: () => api<BomListResponse>(`/boms?page=1&pageSize=100${official ? "&status=active" : ""}`), enabled: open });

  useEffect(() => {
    if (!open) return;
    setTitle("");
    setOfficial(false);
    setSnapshotAt("");
    setWarehouseIds([]);
    setLines([blankLine()]);
    setError("");
  }, [open]);

  function updateLine(index: number, patch: Partial<PlanLine>) { setLines((current) => current.map((line, lineIndex) => lineIndex === index ? { ...line, ...patch } : line)); }
  function scopedBoms(line: PlanLine) { return (boms.data?.items ?? []).filter((bom) => bom.product.id === line.productId && bom.customer.id === line.customerId && (!official || bom.status === "active")); }
  function updateScope(index: number, patch: Partial<PlanLine>) {
    setLines((current) => current.map((line, lineIndex) => {
      if (lineIndex !== index) return line;
      const next = { ...line, ...patch, bomVersionId: "" };
      const suggested = (boms.data?.items ?? []).find((bom) => bom.product.id === next.productId && bom.customer.id === next.customerId && (!official || bom.status === "active"));
      return { ...next, bomVersionId: suggested?.id ?? "" };
    }));
  }
  function selectBom(index: number, bomVersionId: string) {
    updateLine(index, { bomVersionId });
  }
  async function calculate(event: React.FormEvent) {
    event.preventDefault();
    setError("");
    const unavailableLine = lines.findIndex((line) =>
      Boolean(line.productId && line.customerId) && !scopedBoms(line).length,
    );
    if (unavailableLine >= 0) {
      setError(
        official
          ? `Dòng ${unavailableLine + 1} chưa có BOM đang áp dụng cho sản phẩm và khách hàng đã chọn. Hãy gửi duyệt BOM trước hoặc đổi nguồn định mức sang “Bản đang soạn”.`
          : `Dòng ${unavailableLine + 1} chưa có BOM phù hợp cho sản phẩm và khách hàng đã chọn.`,
      );
      return;
    }
    setRunning(true);
    try {
      const output = await api<CalculationResponse>("/calculations", { method: "POST", body: JSON.stringify({ title, isOfficial: official, inventorySnapshotAt: snapshotAt ? new Date(snapshotAt).toISOString() : null, warehouseIds, lines }) });
      await client.invalidateQueries({ queryKey: ["calculation-vouchers"] });
      onCreated(output.id);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "Không thể lập phiếu tính.");
    } finally {
      setRunning(false);
    }
  }
  return <Modal open={open} title="Tính mới nhu cầu vật tư" onClose={() => { if (!running) onClose(); }}>
    <form className="calculation-form calculation-form-modal" onSubmit={calculate}>
      <FormField label="Tên phiếu"><input required value={title} onChange={(event) => setTitle(event.target.value)} placeholder="Ví dụ: Dự trù hợp đồng HD-2026-001" /></FormField>
      <div className="form-grid">
        <FormField label="Nguồn định mức"><select value={official ? "official" : "draft"} onChange={(event) => { setOfficial(event.target.value === "official"); setLines((current) => current.map((line) => ({ ...line, bomVersionId: "" }))); }}><option value="draft">Bản đang soạn</option><option value="official">BOM đang áp dụng</option></select></FormField>
        <FormField label="Thời điểm đối chiếu tồn"><input type="datetime-local" value={snapshotAt} onChange={(event) => setSnapshotAt(event.target.value)} /></FormField>
      </div>
      <div className="form-field"><span>Kho sử dụng tồn</span><div className="checkbox-list">{catalogs?.warehouse.length ? catalogs.warehouse.map((warehouse) => <label key={warehouse.id}><input type="checkbox" checked={warehouseIds.includes(warehouse.id)} onChange={(event) => setWarehouseIds((ids) => event.target.checked ? [...ids, warehouse.id] : ids.filter((id) => id !== warehouse.id))} /> {warehouse.name}</label>) : <span className="muted">Chưa có kho. Hãy tạo kho và nhập tồn kho trước khi chốt phiếu.</span>}</div></div>
      <div className="plan-lines"><div className="form-section-title"><span>Kế hoạch sản xuất</span><Button type="button" variant="outline" onClick={() => setLines((current) => [...current, blankLine()])}><Plus size={15} /> Thêm dòng</Button></div>
        {lines.map((line, index) => {
          const candidates = scopedBoms(line);
          return <div className="plan-line voucher-plan-line" key={index}>
            <CatalogSelect label="Sản phẩm" value={line.productId} onChange={(productId) => updateScope(index, { productId })} items={catalogs?.product ?? []} required />
            <CatalogSelect label="Khách hàng" value={line.customerId} onChange={(customerId) => updateScope(index, { customerId })} items={catalogs?.customer ?? []} required />
            <FormField label="Số hợp đồng"><input value={line.contractNo} onChange={(event) => updateScope(index, { contractNo: event.target.value })} /></FormField>
            <FormField label="Số lượng sản xuất"><NumericInput required value={line.productionQuantity} onValueChange={(productionQuantity) => updateLine(index, { productionQuantity })} /></FormField>
            <FormField label="BOM sử dụng" className="calculation-bom-choice"><select value={line.bomVersionId} disabled={!line.productId || !line.customerId || boms.isLoading} onChange={(event) => selectBom(index, event.target.value)}><option value="">Tự chọn theo sản phẩm và khách hàng</option>{candidates.map((bom) => <option key={bom.id} value={bom.id}>{bom.name || bom.code} · phiên bản {bom.versionNo}</option>)}</select>{line.productId && line.customerId && !candidates.length ? <small className="form-help form-help-error">{official ? "Chưa có BOM đang áp dụng cho sản phẩm và khách hàng này. Hãy gửi duyệt BOM hoặc đổi sang “Bản đang soạn”." : "Chưa có BOM cho sản phẩm và khách hàng này."}</small> : null}</FormField>
            {lines.length > 1 && <button className="remove-plan" type="button" onClick={() => setLines((current) => current.filter((_, lineIndex) => lineIndex !== index))} title="Xóa dòng"><Trash2 size={16} /></button>}
          </div>;
        })}
      </div>
      {error && <p className="form-error">{error}</p>}
      <div className="modal-actions"><Button type="button" variant="outline" onClick={onClose} disabled={running}>Hủy</Button><Button type="submit" loading={running} disabled={!catalogs || boms.isLoading || lines.some((line) => Boolean(line.productId && line.customerId) && !scopedBoms(line).length)}>Tính và lập phiếu</Button></div>
    </form>
  </Modal>;
}
